import React from 'react';
import { X } from 'lucide-react';

interface UnitsFiltersProps {
  filters: any;
  blocks: any[];
  onChange: (filters: any) => void;
}

const UnitsFilters: React.FC<UnitsFiltersProps> = ({ filters, blocks, onChange }) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...filters, [field]: value });
  };

  const handleReset = () => {
    onChange({
      block_id: '',
      unit_type: '',
      status: '',
      min_area: '',
      max_area: '',
      min_floor: '',
      max_floor: '',
      orientation: '',
      view: '',
      has_balcony: false,
      has_parking: false,
      min_price: '',
      max_price: '',
    });
  };

  const activeFiltersCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'has_balcony' || key === 'has_parking') return value === true;
    return value !== '' && value !== false;
  }).length;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 space-y-6 sticky top-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Filtres
        </h3>
        {activeFiltersCount > 0 && (
          <button
            onClick={handleReset}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center space-x-1"
          >
            <X className="w-4 h-4" />
            <span>Réinitialiser ({activeFiltersCount})</span>
          </button>
        )}
      </div>

      {/* Bloc */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Bloc
        </label>
        <select
          value={filters.block_id}
          onChange={(e) => handleChange('block_id', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="">Tous les blocs</option>
          {blocks.map((block) => (
            <option key={block.id} value={block.id}>
              Bloc {block.block_name}
            </option>
          ))}
        </select>
      </div>

      {/* Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Type
        </label>
        <select
          value={filters.unit_type}
          onChange={(e) => handleChange('unit_type', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="">Tous les types</option>
          <option value="studio">Studio</option>
          <option value="f2">F2</option>
          <option value="f3">F3</option>
          <option value="f4">F4</option>
          <option value="f5">F5</option>
          <option value="commercial">Commercial</option>
        </select>
      </div>

      {/* Statut */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Statut
        </label>
        <select
          value={filters.status}
          onChange={(e) => handleChange('status', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="">Tous les statuts</option>
          <option value="available">Disponible</option>
          <option value="reserved">Réservé</option>
          <option value="sold">Vendu</option>
          <option value="unavailable">Indisponible</option>
        </select>
      </div>

      {/* Surface */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Surface (m²)
        </label>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="number"
            placeholder="Min"
            value={filters.min_area}
            onChange={(e) => handleChange('min_area', e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <input
            type="number"
            placeholder="Max"
            value={filters.max_area}
            onChange={(e) => handleChange('max_area', e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      {/* Étage */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Étage
        </label>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="number"
            placeholder="Min"
            value={filters.min_floor}
            onChange={(e) => handleChange('min_floor', e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <input
            type="number"
            placeholder="Max"
            value={filters.max_floor}
            onChange={(e) => handleChange('max_floor', e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      {/* Orientation */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Orientation
        </label>
        <select
          value={filters.orientation}
          onChange={(e) => handleChange('orientation', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="">Toutes</option>
          <option value="nord">Nord</option>
          <option value="sud">Sud</option>
          <option value="est">Est</option>
          <option value="ouest">Ouest</option>
          <option value="nord-est">Nord-Est</option>
          <option value="nord-ouest">Nord-Ouest</option>
          <option value="sud-est">Sud-Est</option>
          <option value="sud-ouest">Sud-Ouest</option>
        </select>
      </div>

      {/* Vue */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Vue
        </label>
        <input
          type="text"
          placeholder="Ex: Mer, Montagne..."
          value={filters.view}
          onChange={(e) => handleChange('view', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>

      {/* Prix */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Prix (DA)
        </label>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="number"
            placeholder="Min"
            value={filters.min_price}
            onChange={(e) => handleChange('min_price', e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <input
            type="number"
            placeholder="Max"
            value={filters.max_price}
            onChange={(e) => handleChange('max_price', e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      {/* Checkboxes */}
      <div className="space-y-2">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={filters.has_balcony}
            onChange={(e) => handleChange('has_balcony', e.target.checked)}
            className="mr-2"
          />
          <span className="text-sm text-gray-900 dark:text-white">Avec balcon</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={filters.has_parking}
            onChange={(e) => handleChange('has_parking', e.target.checked)}
            className="mr-2"
          />
          <span className="text-sm text-gray-900 dark:text-white">Avec parking</span>
        </label>
      </div>
    </div>
  );
};

export default UnitsFilters;
